import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminService } from '@/services/admin';
import { exportService } from '@/services/export';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { format } from 'date-fns';
import { FileText, Download, Loader2, AlertCircle } from 'lucide-react';

export default function TeacherReportGenerator() {
    const [year, setYear] = useState(new Date().getFullYear());
    const [isGenerated, setIsGenerated] = useState(false);

    const { data: reportData, isLoading, error } = useQuery({
        queryKey: ['admin-teacher-yearly-overview', year],
        queryFn: () => adminService.getTeacherYearlyAttendanceOverview({ year }),
        enabled: isGenerated,
    });

    const handleGenerate = () => {
        setIsGenerated(true);
    };

    const handleDownloadPDF = () => {
        if (!reportData) return;

        const headers = ['Month', 'Working Days', 'Present', 'Absent', 'Late', 'Percentage'];
        const rows = reportData.map((m: any) => [
            m.month,
            m.working_days,
            m.present,
            m.absent,
            m.late,
            `${m.percentage}%`
        ]);

        // Calculate summary
        const totalPresent = reportData.reduce((acc: number, curr: any) => acc + curr.present, 0);
        const totalAbsent = reportData.reduce((acc: number, curr: any) => acc + curr.absent, 0);
        const avgPercentage = (reportData.reduce((acc: number, curr: any) => acc + (curr.percentage || 0), 0) / 12).toFixed(1);

        exportService.generatePDF({
            title: `Teacher Attendance Report - ${year}`,
            subtitle: `Annual School-wide Teacher Performance`,
            headers,
            rows,
            summary: [
                { label: 'Yearly Avg', value: `${avgPercentage}%` },
                { label: 'Total Present', value: totalPresent },
                { label: 'Total Absent', value: totalAbsent },
                { label: 'Total Working Days', value: reportData.reduce((acc: number, curr: any) => acc + curr.working_days, 0) },
            ]
        });
    };

    return (
        <Card className="p-6">
            <div className="flex items-center gap-2 mb-6">
                <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                    <FileText size={20} />
                </div>
                <h3 className="text-lg font-bold text-slate-800">Generate Teacher Annual Report</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Select Year</label>
                    <select
                        className="w-full rounded-lg border-slate-200 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        value={year}
                        onChange={(e) => {
                            setYear(Number(e.target.value));
                            setIsGenerated(false);
                        }}
                    >
                        {[2024, 2025, 2026].map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
                <div className="flex items-end">
                    <Button 
                        onClick={handleGenerate} 
                        className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold h-[38px]"
                        disabled={isLoading}
                    >
                        {isLoading ? <Loader2 className="animate-spin" size={18} /> : 'Preview Report'}
                    </Button>
                </div>
            </div>

            {error && (
                <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg border border-red-100">
                    <AlertCircle size={18} />
                    <p className="text-sm">Could not load the teacher report. Try another year, then Preview again.</p>
                </div>
            )}

            {isGenerated && reportData && (
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 mt-6 animate-in fade-in slide-in-from-top-4">
                    <div className="flex justify-between items-center">
                        <div>
                            <h4 className="font-bold text-slate-900 text-lg">Annual Teacher Report {year}</h4>
                            <p className="text-sm text-slate-500">Full school teacher attendance summary</p>
                        </div>
                        <Button 
                            onClick={handleDownloadPDF}
                            className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
                        >
                            <Download size={18} />
                            <span>Download PDF</span>
                        </Button>
                    </div>

                    <div className="mt-6 overflow-x-auto">
                        <table className="w-full text-xs text-left text-slate-500">
                            <thead className="text-slate-700 uppercase bg-slate-100 font-bold">
                                <tr>
                                    <th className="px-4 py-2">Month</th>
                                    <th className="px-4 py-2">Present</th>
                                    <th className="px-4 py-2">Absent</th>
                                    <th className="px-4 py-2">Late</th>
                                    <th className="px-4 py-2">Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.slice(0, 6).map((m: any) => (
                                    <tr key={m.month} className="border-b border-slate-200">
                                        <td className="px-4 py-2 font-medium text-slate-900">{m.month}</td>
                                        <td className="px-4 py-2 text-green-600">{m.present}</td>
                                        <td className="px-4 py-2 text-rose-500">{m.absent}</td>
                                        <td className="px-4 py-2 text-amber-500">{m.late}</td>
                                        <td className="px-4 py-2 font-bold">{m.percentage}%</td>
                                    </tr>
                                ))}
                                <tr className="bg-white">
                                    <td colSpan={5} className="px-4 py-2 text-center italic text-slate-400">... and {reportData.length - 6} more months</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </Card>
    );
}
